import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Dropdown, Icon, Label, Loader, Message, Table } from "semantic-ui-react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    CompanyRole,
    getCompanyEmployeesThunk,
    selectCompanyEmployeesState,
    setCompanyEmployeeRoleThunk,
} from "../../slices/companyEmployeesSlice";
import { selectUser } from "../../slices/userSlice";

const ROLE_OPTIONS = [
    { key: 'admin', value: 'admin', text: 'Admin', description: 'Can change company settings and outreach rules' },
    { key: 'member', value: 'member', text: 'Member', description: 'Can view and rate' },
];

/*
* The company's people and what each of them can do here.
*
* Employment comes from Procore - this screen only decides who runs Sub Ratings
* for the company. The first employee to sign in becomes the admin
* automatically; everyone after that is a member until an admin says otherwise.
*/
const CompanyPeople = () => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const companyId = parseInt(params?.companyId!);
    const { data: employees, status, roleChangingUserId, error } = useAppSelector(selectCompanyEmployeesState);
    const user = useAppSelector(selectUser);
    const isAdmin = user?.companies?.find(c => c.id === companyId)?.isAdmin === true;

    useEffect(() => {
        dispatch(getCompanyEmployeesThunk(companyId));
    }, [companyId]);

    const changeRole = (userId: number, role: CompanyRole) => {
        dispatch(setCompanyEmployeeRoleThunk({ companyId, userId, role }));
    };

    if (status === 'loading' && !employees.length) {
        return <Loader active inline='centered' />;
    }

    return (
        <StyledCompanyPeople id='company-people'>
            <p>
                Admins can change company settings, custom rating categories and outreach rules.
                Members can view and rate. The first person to sign in for a company becomes its admin.
            </p>

            { error?.reason && <Message negative size='tiny' content={error.reason} /> }

            <Table basic='very' compact>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Name</Table.HeaderCell>
                        <Table.HeaderCell>Email</Table.HeaderCell>
                        <Table.HeaderCell>Role</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {
                        employees.map(employee => (
                            <Table.Row key={employee.id}>
                                <Table.Cell>
                                    {employee.firstName} {employee.lastName}
                                    { String(employee.id) === String(user?.id) && <StyledYou size='tiny'>you</StyledYou> }
                                </Table.Cell>
                                <Table.Cell>{employee.email}</Table.Cell>
                                <Table.Cell>
                                    {
                                        isAdmin ?
                                            <Dropdown
                                                selection
                                                compact
                                                options={ROLE_OPTIONS}
                                                value={employee.role}
                                                loading={roleChangingUserId === employee.id}
                                                disabled={roleChangingUserId !== undefined}
                                                onChange={(e, x) => changeRole(employee.id, x.value as CompanyRole)}
                                            />
                                            :
                                            <Label size='tiny' basic>{employee.role === 'admin' ? 'Admin' : 'Member'}</Label>
                                    }
                                </Table.Cell>
                            </Table.Row>
                        ))
                    }
                    {
                        !employees.length &&
                        <Table.Row>
                            <Table.Cell colSpan={3}>No employees synced from Procore yet.</Table.Cell>
                        </Table.Row>
                    }
                </Table.Body>
            </Table>

            {
                !isAdmin &&
                <p><Icon name='lock' /> Only a company admin can change roles.</p>
            }
        </StyledCompanyPeople>
    );
};

const StyledYou = styled(Label)`
    &&& {
        margin-left: 8px;
    }
`;

const StyledCompanyPeople = styled('div')`
    max-width: 750px;
`;

export default CompanyPeople;
